import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/pages/Login";
import { toast } from "sonner";
import { HardHat, User, Gift } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").trim().toUpperCase();
  const [role, setRole] = useState("manager");
  const [form, setForm] = useState({ name: "", email: "", password: "", fleet_name: "", invite_code: "" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) { toast.error("Please agree to the Terms of Service and Privacy Policy."); return; }
    setLoading(true);
    const payload = { name: form.name, email: form.email, password: form.password, role, accept_terms: true };
    if (role === "manager") { payload.fleet_name = form.fleet_name; if (refCode) payload.ref_code = refCode; }
    else payload.invite_code = form.invite_code;
    const res = await register(payload);
    setLoading(false);
    if (res.ok) { toast.success("Fleet ready — welcome to IronLog"); nav("/dashboard"); }
    else toast.error(res.error);
  };

  return (
    <AuthShell title="Create Account" sub="Start a fleet or join your team.">
      {refCode && role === "manager" && (
        <div className="card p-4 mb-5 border-l-4 border-[#00E676] flex items-center gap-3" data-testid="referral-banner">
          <Gift className="w-5 h-5 text-[#00E676] shrink-0" />
          <p className="font-body text-sm text-white">You were invited with code <span className="font-mono text-[#00E676]">{refCode}</span> — your <span className="font-700">first month is on us</span> when you upgrade.</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 mb-6" data-testid="role-toggle">
        <button type="button" onClick={() => setRole("manager")} className={`h-16 border flex flex-col items-center justify-center gap-1 transition-colors ${role === "manager" ? "bg-[#FFB800] text-black border-[#FFB800]" : "card text-white border-white/20"}`} data-testid="role-manager">
          <HardHat className="w-5 h-5" /><span className="font-head font-700 uppercase text-sm">Manager</span>
        </button>
        <button type="button" onClick={() => setRole("worker")} className={`h-16 border flex flex-col items-center justify-center gap-1 transition-colors ${role === "worker" ? "bg-[#FFB800] text-black border-[#FFB800]" : "card text-white border-white/20"}`} data-testid="role-worker">
          <User className="w-5 h-5" /><span className="font-head font-700 uppercase text-sm">Worker</span>
        </button>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <label className="il-label">Full Name</label>
          <input className="il-input" value={form.name} onChange={set("name")} required data-testid="register-name" placeholder="Dave Whitmore" />
        </div>
        <div>
          <label className="il-label">Email</label>
          <input className="il-input" type="email" value={form.email} onChange={set("email")} required data-testid="register-email" placeholder="you@fleet.com" />
        </div>
        <div>
          <label className="il-label">Password</label>
          <input className="il-input" type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="register-password" placeholder="Min 6 characters" />
        </div>
        {role === "manager" ? (
          <div>
            <label className="il-label">Fleet / Organization Name</label>
            <input className="il-input" value={form.fleet_name} onChange={set("fleet_name")} required data-testid="register-fleet-name" placeholder="Whitmore Contracting" />
          </div>
        ) : (
          <div>
            <label className="il-label">Fleet Invite Code</label>
            <input className="il-input font-mono uppercase" value={form.invite_code} onChange={(e) => setForm({ ...form, invite_code: e.target.value.toUpperCase() })} required data-testid="register-invite-code" placeholder="6-CHAR CODE" maxLength={6} />
          </div>
        )}
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="accept-terms-label">
          <input
            type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#FFB800]" data-testid="accept-terms-checkbox"
          />
          <span className="font-body text-sm text-[#94A3B8]">
            I agree to the <Link to="/terms" target="_blank" className="text-[#FFB800] hover:underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-[#FFB800] hover:underline">Privacy Policy</Link>.
          </span>
        </label>
        <button className="btn-signal w-full h-14 font-head text-lg" disabled={loading} data-testid="register-submit">
          {loading ? "Setting up…" : role === "manager" ? "Create Fleet" : "Join Fleet"}
        </button>
      </form>
      <p className="font-body text-[#94A3B8] mt-6 text-sm">Already have an account? <Link to="/login" className="text-[#FFB800] font-700" data-testid="goto-login">Log in →</Link></p>
    </AuthShell>
  );
}
